import Button from './button'
import { useCallback, type JSX } from 'react'
import { useCurrencyFormatter } from '../hooks/formatter'
import { ExchangeRates } from '../services/bookings'
import { useTranslation } from 'react-i18next'
import dayjs from '../libs/dayjs'

function InteracStep ({
  index,
  title,
  value,
  copyValue,
  description
}: {
  index: number
  title: string
  value: string
  copyValue?: string
  description: string
}): JSX.Element {
  const { t } = useTranslation()
  const copy = useCallback(() => {
    void navigator.clipboard.writeText(copyValue ?? value)
  }, [value, copyValue])
  return (
    <div className='flex flex-wrap gap-4 bg-white border border-yellow-400 rounded-sm p-4 items-start'>
      <div>
        <div
          className='size-6 text-xs bg-primary text-white flex justify-center items-center rounded-full'
        >{index}
        </div>
      </div>
      <div className='grow flex flex-col gap-1'>
        <div className='uppercase text-neutral-500 text-sm'>{title}</div>
        <div className='font-bold'>{value}</div>
        <div className='text-neutral-500 text-sm'>{description}</div>
      </div>
      <div>
        <Button className='cursor-pointer' outline onClick={copy}>{t('payment:interac.copy')}</Button>
      </div>
    </div>
  )
}

export function InteracPayment ({ bookingNumber, amount, exchangeRate, onComplete }: {
  amount: number
  bookingNumber: string
  exchangeRate: ExchangeRates[string]
  onComplete?: () => void
}): JSX.Element {
  const { t } = useTranslation()
  const eurFormatter = useCurrencyFormatter('EUR')
  const cadFormatter = useCurrencyFormatter('CAD')
  const cadExchangeFormatter = new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
    currencyDisplay: 'code'
  })
  const eurExchangeFormatter = new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    currencyDisplay: 'code'
  })
  const copyNumberFormatter = new Intl.NumberFormat('en-CA', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    useGrouping: false
  })
  const cadAmount = amount * exchangeRate.rate
  const validUntil = dayjs(exchangeRate.valid_until).format('LL')
  const contactHref = `mailto:info@boatbiketours.com?subject=Question+about+booking+${bookingNumber}`
  return (
    <div>
      <div className='bg-yellow-50 rounded-md border border-yellow-400 p-6 flex flex-col gap-4 mb-8'>
        <h4 className='text-2xl font-bold'>{t('payment:interac.title')}</h4>
        <p className='text-neutral-500 text-sm'>{t('payment:interac.description')}</p>

        <div>
          <span className='text-3xl font-bold'>{cadFormatter.format(cadAmount / 100)}</span>
          <span> </span>
          <span className='text-neutral-500 text-sm'>≈ {eurFormatter.format(amount / 100)}</span>
        </div>
        <div className='text-neutral-500 text-sm'>
          {t('payment:interac.converted_at', {
            eur: eurExchangeFormatter.format(1),
            cad: cadExchangeFormatter.format(exchangeRate.rate),
            valid_until: validUntil
          })}
        </div>
        <InteracStep
          index={1}
          title={t('payment:interac.steps.recipient.title')}
          description={t('payment:interac.steps.recipient.hint')}
          value='payments@boatbiketours.com'
        />
        <InteracStep
          index={2}
          title={t('payment:interac.steps.amount.title')}
          description={t('payment:interac.steps.amount.hint')}
          value={cadFormatter.format(cadAmount / 100)}
          copyValue={copyNumberFormatter.format(cadAmount / 100)}
        />
        <InteracStep
          index={3}
          title={t('payment:interac.steps.reference.title')}
          description={t('payment:interac.steps.reference.hint')}
          value={bookingNumber}
        />
        <div
          dangerouslySetInnerHTML={{
            __html: t('payment:interac.quote_validity', { valid_until: validUntil })
          }}
        />
        <p
          dangerouslySetInnerHTML={{
            __html: t('payment:interac.contact', { href: contactHref })
          }}
        />
      </div>
      <div>
        <Button className='cursor-pointer' onClick={() => onComplete?.()}>{t('payment:interac.sent')}</Button>
      </div>
    </div>
  )
}
